/* eslint-disable react/require-default-props */
import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  Typography, Card, CardMedia, CardContent,
  Skeleton, SkeletonContent, SkeletonLine, SkeletonBlock,
  Button
} from '@one-thd/sui-atomic-components';
import * as Icons from '@one-thd/sui-icons';
import { AppContext } from '@thd-nucleus/app-render';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import { useImpression } from '@thd-olt-component-react/impression';
import PropTypes from 'prop-types';
import { selectPreferredImageSrc } from '../utils/selectPreferredImage';

const ShopByCategoryRenderer = ({
  data = null,
  loading = false,
  categoryList = [],
  enableB2CGapAndPadding = false
}) => {

  const title = 'Shop By Category';
  const { channel } = useContext(ExperienceContext);
  const [showMore, setShowMore] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const { instance } = useContext(AppContext) || {};
  // SEO bots need the categories in the server-rendered HTML, so they skip the hydration gate.
  const canRender = isHydrated || !!instance?.thdSEOBotDetection;
  // Keep SSR and first client render in sync to avoid hydration mismatches.
  const isMobile = isHydrated && channel === 'mobile';
  const categoryRef = useRef(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const OnViewLessClick = () => {
    setShowMore(false);
    if (categoryRef?.current) {
      categoryRef.current.scrollIntoView({
        behavior: 'auto'
      });
    }
  };
  /* eslint-disable react/prop-types */
  const CustomImage = ({ imgSrc, imgAlt }) => {
    return (
      <img
        className="sui-aspect-square"
        src={imgSrc}
        alt={imgAlt}
        height="24"
        width="24"
      />
    );
  };

  const ShoppingCategoryCard = (category, index) => {
    const { ref, clickID } = useImpression({
      data: {
        id: category?.id || '',
        name: 'ShoppingCategories',
        component: 'ShoppingCategories',
        position: index + 1,
        type: 'content',
        category: category?.title
      }
    });
    const stencilIconValue = category?.stencilIcon;
    const showStencilIcon = stencilIconValue !== undefined
      && stencilIconValue !== null
      && stencilIconValue !== '';
    const StencilIcon = showStencilIcon ? Icons[stencilIconValue] : <></>;

    const imgSrc = selectPreferredImageSrc({
      damImage: category?.previewImage,
      defaultImageUrl: category?.icon?.url
    });
    const cardClassNames = classNames('sui-h-full sui-content-center', {
      '!sui-rounded-[12px]': enableB2CGapAndPadding
    });
    return (
      <div
        ref={ref}
        // eslint-disable-next-line react/no-unknown-property
        clickid={clickID}
        key={index}
        data-testid={`category ${index}`}
      >
        <a
          href={category?.link}
        >
          <Card
            disablePadding
            disableGutters
            className={cardClassNames}
            value={category?.id}
            aria-label={category?.id}
            orientation="horizontal"
            data-contentful-entry-id={category?.id}
            data-contentful-field-id="internalName"
          >
            <CardContent orientation="horizontal" disableGutters grow>
              <div className="sui-flex sui-items-center">
                <div className="sui-flex sui-grow-0 sui-shrink-0 sui-basis-auto sui-mr-2">
                  {showStencilIcon ? (
                    <StencilIcon
                      data-contentful-entry-id={category?.id}
                      data-contentful-field-id="stencilIcon"
                    />
                  ) : (
                    <CardMedia
                      component={(() => <CustomImage imgSrc={imgSrc} imgAlt={`${category?.title} Placeholder`} />
                      )}
                    />
                  )}
                </div>
                <div
                  className="sui-flex-auto sui-text-base sui-leading-normal sui-font-bold"
                  data-contentful-entry-id={category?.id}
                  data-contentful-field-id="title"
                >
                  {category?.title}
                </div>
              </div>
            </CardContent>
          </Card>
        </a>
      </div>
    );
  };

  const LoadingPlaceholders = () => {
    const placeHolderCount = isMobile ? 8 : 18;
    const placeholderElements = [];
    for (let i = 0; i < placeHolderCount; i += 1) {
      placeholderElements.push(
        <Skeleton
          orientation="horizontal"
          disablePadding
          disableGutters
          key={i}
        >
          <SkeletonContent disableGutters disableShrink orientation="horizontal" grow>
            <div className="sui-flex sui-items-center">
              <div className="sui-flex sui-grow-0 sui-shrink-0 sui-basis-auto sui-mr-2">
                <SkeletonBlock aspect="square" height={6} width={6} />
              </div>
              <div className="sui-flex-auto sui-text-base sui-leading-normal sui-font-bold">
                <SkeletonLine variant="single" fullWidth />
              </div>
            </div>
          </SkeletonContent>
        </Skeleton>
      );
    }
    if (isMobile) {
      placeholderElements.push(
        <div className="sui-flex sui-items-center sui-justify-center" key={9}>
          <SkeletonBlock aspect="wide" height={11} width={24} />
        </div>);
    }
    return placeholderElements;
  };

  const ShoppingCategories = () => {
    const numberofCategory = (showMore || !isMobile) ? categoryList?.length : 8;
    let reducedCategoryList = categoryList?.slice(0, numberofCategory)?.map((category, index) => {
      return ShoppingCategoryCard(category, index);
    });
    if (isMobile) {
      reducedCategoryList.push(
        <div className="sui-flex sui-items-center sui-justify-center" key={9}>
          {showMore
            ? (
              <Button
                variant="text"
                endIcon={Icons.ArrowUp}
                onClick={OnViewLessClick}
              >
                Show Less
              </Button>
            )
            : (
              <Button
                variant="text"
                endIcon={Icons.ArrowDown}
                onClick={() => { setShowMore(true); }}
              >
                Show More
              </Button>
            )}
        </div>
      );
    }
    return reducedCategoryList;
  };

  const gridStyles = classNames('sui-grid sui-grid-cols-1 sui-auto-rows-fr sm:sui-grid-cols-3 lg:sui-grid-cols-5',
    'sui-gap-4 sui-mt-5',
    { 'md:sui-gap-6 xl:sui-gap-8': enableB2CGapAndPadding });
  const showLoadingPlaceholders = !canRender || (loading && !data);
  const showShoppingCategories = canRender && !!data;

  return (
    <div
      className={enableB2CGapAndPadding ? 'sui-grid' : 'sui-grid sui-p-2'}
      ref={categoryRef}
      data-component="ShopByCategory"
    >
      <Typography variant="h2">
        {title}
      </Typography>
      {showLoadingPlaceholders && (
        <div className={gridStyles}>
          <LoadingPlaceholders />
        </div>
      )}
      {showShoppingCategories && (
        <div className={gridStyles}>
          <ShoppingCategories />
        </div>
      )}
    </div>
  );
};

ShopByCategoryRenderer.propTypes = {
  data: PropTypes.shape({
    component: PropTypes.shape({
      shopByCategoryNavigationItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        link: PropTypes.string,
        previewImage: PropTypes.shape({}),
        icon: PropTypes.shape({ url: PropTypes.string }),
        stencilIcon: PropTypes.string
      }))
    })
  }),
  loading: PropTypes.bool,
  categoryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.string,
    previewImage: PropTypes.shape({}),
    icon: PropTypes.shape({ url: PropTypes.string }),
    stencilIcon: PropTypes.string
  })),
  enableB2CGapAndPadding: PropTypes.bool
};

export { ShopByCategoryRenderer };
